import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui';
import {
  Bug,
  ChevronLeft,
  Home as HomeIcon,
  LayoutDashboard,
  LogOut,
  Menu as MenuIcon,
  Database,
} from './ui/icons';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [open, setOpen] = useState(true);
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  const toggleDrawer = () => {
    setOpen(!open);
  };

  const handleLogout = async () => {
    await logout();
  };

  const menuItems = [
    { text: 'Dashboard', icon: <LayoutDashboard className="h-5 w-5" />, path: '/' },
    { text: 'Sources', icon: <Database className="h-5 w-5" />, path: '/sources' },
    { text: 'Properties', icon: <HomeIcon className="h-5 w-5" />, path: '/properties' },
    { text: 'System Logs', icon: <Bug className="h-5 w-5" />, path: '/system-logs' },
  ];

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 bg-white border-r border-gray-200 transition-all duration-300 ${
          open ? 'w-60' : 'w-16 sm:w-18'
        }`}
      >
        {/* Sidebar Header */}
        <div className="flex items-center justify-end h-16 px-2 border-b border-gray-200">
          <button
            onClick={toggleDrawer}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-700 transition-colors"
            aria-label={open ? 'Close drawer' : 'Open drawer'}
          >
            <ChevronLeft className={`h-5 w-5 transition-transform duration-300 ${open ? '' : 'rotate-180'}`} />
          </button>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 py-4">
          {menuItems.map((item) => (
            <button
              key={item.text}
              onClick={() => navigate(item.path)}
              className={`flex items-center w-full px-4 py-3 text-gray-700 hover:bg-primary-50 hover:text-primary-700 transition-colors ${
                !open ? 'justify-center' : ''
              }`}
            >
              <span className="flex-shrink-0">
                {item.icon}
              </span>
              {open && (
                <span className="ml-3 text-sm font-medium">
                  {item.text}
                </span>
              )}
            </button>
          ))}
        </nav>
      </aside>

      {/* Main Content Area */}
      <div className={`flex-1 flex flex-col transition-all duration-300 ${open ? 'ml-60' : 'ml-16 sm:ml-18'}`}>
        {/* Top AppBar */}
        <header className="fixed top-0 right-0 z-40 h-16 bg-primary-600 shadow-md transition-all duration-300"
          style={{ left: open ? '15rem' : '4rem' }}
        >
          <div className="flex items-center justify-between h-full px-6">
            {/* Menu button - only show when drawer is closed */}
            {!open && (
              <button
                onClick={toggleDrawer}
                className="p-2 text-white hover:bg-primary-700 rounded-lg transition-colors mr-4"
                aria-label="Open drawer"
              >
                <MenuIcon className="h-5 w-5" />
              </button>
            )}

            {/* Title */}
            <h1 className="text-xl font-semibold text-white flex-1 truncate">
              Country Homes Admin
            </h1>

            {/* User Info and Logout */}
            <div className="flex items-center gap-4">
              {user && (
                <span className="text-sm text-white hidden sm:block">
                  Welcome, {user.email}
                </span>
              )}
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="text-white hover:bg-primary-700"
                size="sm"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-auto bg-gray-100 pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
